import { Component, Input, ContentChild, TemplateRef } from '@angular/core';
import { WritableResource as Resource } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProcessingState } from '@services/core-services/models';
import { SecondaryBtnsComponent } from '@enginuity/core/molecules/secondary-btns/secondary-btns.component';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';

interface ErrorWithMessage {
  message: string;
}

interface ErrorWithCause {
  cause: unknown;
}

@Component({
  selector: 'app-global-loader-wrapper',
  imports: [CommonModule, SecondaryBtnsComponent],
  templateUrl: './global-loader-wrapper.component.html',
  styleUrls: ['./global-loader-wrapper.styles.scss'],
})
export class GlobalLoaderWrapperComponent {
  @Input() resource!: Resource<any>;
  @Input() headingTitle: string = '';
  @Input() subtitle: string = '';
  @Input() emptyMessage: string = 'No data available';
  @ContentChild(TemplateRef) contentTemplate?: TemplateRef<any>;

  protected processingState = ProcessingState;
  protected loaderImagePath: string = '/assets/loader-spin.svg';

  constructor(private readonly errorHandler: ErrorHandlerService) {}

  private isErrorWithMessage(error: unknown): error is ErrorWithMessage {
    return (
      error !== null &&
      typeof error === 'object' &&
      'message' in error &&
      typeof (error as ErrorWithMessage).message === 'string'
    );
  }

  private isErrorWithCause(error: unknown): error is ErrorWithCause {
    return error !== null && typeof error === 'object' && 'cause' in error;
  }

  getErrorMessage(): { title: string; message: string } {
    const error: unknown = this.resource.error();
    if (error instanceof HttpErrorResponse) {
      return this.errorHandler.handleServerError(error);
    }

    if (this.isErrorWithCause(error)) {
      const cause = error.cause;
      if (cause instanceof HttpErrorResponse) {
        return this.errorHandler.handleServerError(cause);
      }
      if (this.isErrorWithMessage(cause)) {
        return {
          title: 'Error',
          message: cause.message,
        };
      }
    }

    if (error instanceof Error) {
      return {
        title: 'Error',
        message: error.message,
      };
    }

    if (this.isErrorWithMessage(error)) {
      return {
        title: 'Error',
        message: error.message,
      };
    }

    return {
      title: 'Error',
      message: 'An unexpected error occurred. Please try again.',
    };
  }
}
